// File: src/pages/Admin/GPT_Cockpit.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import config from "../../config/config";
import AdminPageFrame from "../../components/admin/AdminPageFrame";

const GPTCockpit = () => {
  const [prompt, setPrompt] = useState("Was ist der Zweck des Matching-Moduls?");
  const [response, setResponse] = useState("");
  const [markdown, setMarkdown] = useState("");
  const [filePath, setFilePath] = useState("src/routes/gptRoutes.js");
  const [folderPath, setFolderPath] = useState("src/routes");
  const [docIndex, setDocIndex] = useState([]);
  const [autodocStatus, setAutodocStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("autodoc");

  const loadStatus = async () => {
    try {
      const s = await axios.get(`${config.API_BASE_URL}/api/gpt/autodoc/status`);
      setAutodocStatus(s.data);
    } catch (err) {
      console.error("❌ Fehler beim AutoDoc-Status", err);
    }
  };

  const loadIndex = async () => {
    try {
      const res = await axios.get(`${config.API_BASE_URL}/api/gpt/doc/index`);
      setDocIndex(res.data.index || []);
    } catch (err) {
      console.error("❌ Fehler beim Laden der Indexdaten", err);
    }
  };

  useEffect(() => {
    loadStatus();
    loadIndex();
  }, []);

  const runPrompt = async () => {
    setLoading(true);
    setResponse("");
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/gpt/task`, {
        task: prompt,
        language: "de",
        role: "system"
      });
      setResponse(res.data.result);
    } catch {
      setResponse("❌ Fehler beim GPT-Aufruf");
    }
    setLoading(false);
  };

  const runAutoDoc = async () => {
    setLoading(true);
    setMarkdown("🚀 AutoDoc wird ausgeführt...");
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/gpt/autodoc/trigger`);
      setMarkdown(`✅ ${res.data.result.updated} Datei(en) aktualisiert.`);
      loadStatus();
      loadIndex();
    } catch {
      setMarkdown("❌ Fehler bei AutoDoc.");
    }
    setLoading(false);
  };

  const runDocFile = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/gpt/doc/file`, { filePath });
      setMarkdown(`✅ Datei dokumentiert: ${res.data.file}`);
      loadIndex();
    } catch {
      setMarkdown("❌ Fehler bei Dateidokumentation");
    }
    setLoading(false);
  };

  const runDocFolder = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${config.API_BASE_URL}/api/gpt/doc/folder`, { folder: folderPath });
      setMarkdown(`📂 ${res.data.processed} Datei(en) verarbeitet`);
      loadIndex();
    } catch {
      setMarkdown("❌ Fehler bei Ordner-Dokumentation");
    }
    setLoading(false);
  };

  const updateContext = async () => {
    setLoading(true);
    try {
      await axios.post(`${config.API_BASE_URL}/api/gpt/context/update`);
      alert("✅ Kontext aktualisiert");
    } catch {
      alert("❌ Fehler beim Aktualisieren des Kontextes");
    }
    setLoading(false);
  };

  return (
    <AdminPageFrame
      title="GPT Cockpit"
      subtitle="AutoDoc, Dokumentationspfade, GPT-Tasks und Indexzugriff in einer gemeinsamen AI-Shell."
      badge="AI Operations"
    >
      <div className="admin-workbench">
        <div className="admin-workbench__tabs">
          <button type="button" onClick={() => setSection("autodoc")} className={`admin-workbench__tab${section === "autodoc" ? " is-active" : ""}`}>AutoDocEngine</button>
          <button type="button" onClick={() => setSection("doc")} className={`admin-workbench__tab${section === "doc" ? " is-active" : ""}`}>Dokumentation</button>
          <button type="button" onClick={() => setSection("prompt")} className={`admin-workbench__tab${section === "prompt" ? " is-active" : ""}`}>GPT-Task</button>
          <button type="button" onClick={() => setSection("docs")} className={`admin-workbench__tab${section === "docs" ? " is-active" : ""}`}>Docs</button>
        </div>

        {section === "autodoc" && (
          <section className="admin-workbench__panel">
            <div>
              <h3>AutoDocEngine</h3>
              <p className="admin-workbench__copy">
                Letzter Lauf: {autodocStatus?.lastRun ? new Date(autodocStatus.lastRun).toLocaleString("de-DE") : "unbekannt"} | Dateien: {autodocStatus?.files || 0}
              </p>
            </div>
            <div className="admin-workbench__actions">
              <button type="button" onClick={runAutoDoc} disabled={loading} className="admin-workbench__button admin-workbench__button--primary">Jetzt AutoDoc starten</button>
              <button type="button" onClick={updateContext} disabled={loading} className="admin-workbench__button admin-workbench__button--success">Kontext aktualisieren</button>
            </div>
          </section>
        )}

        {section === "doc" && (
          <section className="admin-workbench__panel">
            <div className="admin-workbench__grid admin-workbench__grid--split">
              <div className="admin-workbench__field">
                <label htmlFor="gpt-doc-file">Datei dokumentieren</label>
                <input id="gpt-doc-file" type="text" value={filePath} onChange={(e) => setFilePath(e.target.value)} className="admin-workbench__input" />
                <button type="button" onClick={runDocFile} disabled={loading} className="admin-workbench__button admin-workbench__button--primary">Dokumentieren</button>
              </div>
              <div className="admin-workbench__field">
                <label htmlFor="gpt-doc-folder">Ordner dokumentieren</label>
                <input id="gpt-doc-folder" type="text" value={folderPath} onChange={(e) => setFolderPath(e.target.value)} className="admin-workbench__input" />
                <button type="button" onClick={runDocFolder} disabled={loading} className="admin-workbench__button admin-workbench__button--success">Scannen und dokumentieren</button>
              </div>
            </div>
          </section>
        )}

        {section === "prompt" && (
          <section className="admin-workbench__panel">
            <div className="admin-workbench__field">
              <label htmlFor="gpt-prompt-input">GPT-Eingabe</label>
              <textarea id="gpt-prompt-input" rows={4} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="admin-workbench__textarea" />
            </div>
            <div className="admin-workbench__actions">
              <button type="button" onClick={runPrompt} disabled={loading} className="admin-workbench__button admin-workbench__button--primary">Ausfuehren</button>
            </div>
            {response ? <pre className="admin-workbench__output">{response}</pre> : null}
          </section>
        )}

        {section === "docs" && (
          <section className="admin-workbench__panel">
            <div>
              <h3>Dokumentierte Dateien</h3>
              <p className="admin-workbench__copy">Index und Dokumentpfade bleiben direkt aus dem Cockpit erreichbar.</p>
            </div>
            <ul className="admin-workbench__list">
              {docIndex.length > 0 ? (
                docIndex.map((item, idx) => (
                  <li key={idx}>
                    <a href={item.path} target="_blank" rel="noreferrer">{item.file}</a>
                  </li>
                ))
              ) : (
                <li>Keine Dokumentation gefunden.</li>
              )}
            </ul>
          </section>
        )}

        {markdown ? <pre className="admin-workbench__code">{markdown}</pre> : null}
      </div>
    </AdminPageFrame>
  );
};

export default GPTCockpit;
